<template>
  <div class="trascription-control-wrapper">
    <div
      v-if="
        hasTranscripts ||
        isStartedForCurrentNote ||
        isPendingForCurrentNote ||
        alreadyRecordedForCurrentNote
      "
      v-tooltip="{
        content: translate('viewTranscript'),
        placement: 'top',
        offset: [0, 4],
        onShow: () => !isRecordingPending && !isMobile,
      }"
      class="transcript-button"
      :class="{
        'pointer-events-none': isRecordingPending,
      }"
      @click="handleOpenTranscripts"
    >
      <SuggestionSpinner v-if="isRecordingPending" />
      <AudioPulse
        v-if="isStartedForCurrentNote"
        :key="audioPulseId"
        class="w-4 h-4"
        :paused="!isStartedForCurrentNote"
      />
      <em
        v-if="!isStartedForCurrentNote"
        :style="getCustomIconStyle(iconPath('waveform'))"
        class="custom-icons w-4 h-4"
      />
      <em
        v-if="!isRecordingPending"
        :style="getCustomIconStyle(iconPath('chevron-down'))"
        class="custom-icons w-5 h-5"
      />
    </div>
    <ButtonCustom
      v-tooltip="{
        content: translate(transcriptControlTooltip),
        placement: 'top',
        onShow: () => !isControlTooltipDisabled && !isMobile,
      }"
      :class="{ 'disabled-button pointer-events-auto': isControlLabelDisabled }"
      :text="transcriptControlLabel"
      :type="'secondary-regular secondary-button'"
      :custom-icon-name="transcriptControlIcon"
      :custom-icon-color="'--var(iconDefault)'"
      :custom-icon-size="'16px'"
      :button-dimensions="{ height: '24px' }"
      @click="controlLabelClick"
    />
    <Portal
      v-if="changeTranscriptionNoteIdDialog.isRevealed.value"
      :disabled="isQuickNote"
      to="root-widget"
    >
      <ConfirmationModal
        :title="translate('transcribeHereInstead')"
        :description="
          translate('currentlyTranscribingAnotherNote', {
            noteTitle: currentTranscriptionNoteTitle,
          })
        "
        :confirm-button-text="translate('transcribeHere')"
        @confirm="changeCurrentTranscriptionNoteId"
        @cancel="changeTranscriptionNoteIdDialog.cancel"
      />
    </Portal>
  </div>
</template>

<script setup lang="ts">
import { toRef, ref, computed, watch, onMounted } from 'vue';
import useAudioTranscription from '@/utilities/composables/useAudioTranscription';
import AudioPulse from '@/components/elements/AudioPulse.vue';
import SuggestionSpinner from '@/components/elements/SuggestionSpinner.vue';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import useTranslate from '@/utilities/composables/useTranslate';
import ButtonCustom from '@/components/elements/ButtonCustom';
import useIsElectronApp from '@/utilities/composables/useIsElectronApp';
import usePlatform from '@/utilities/composables/usePlatform';
import { useConfirmDialog } from '@vueuse/core';
import { Portal } from 'portal-vue';
import ConfirmationModal from '@/components/widgets/ConfirmationModal';
const { iconPath, getCustomIconStyle } = useCustomIcon();
import constants from '@/utilities/constants';
import { useStore } from 'vuex';
import useUsageLimits from '@/utilities/composables/useUsageLimits';
import { useRoute } from 'vue-router';
const MAX_MINUTES_DIFF_FOR_AUTO_RECORDING = 5;

const { translate } = useTranslate();
const { isElectronApp } = useIsElectronApp();
const { isMac } = usePlatform();
const store = useStore();
const { routeToUpgradePlan } = useUsageLimits();
const route = useRoute();

const emit = defineEmits(['openTranscripts', 'stopRecording', 'startRecording']);

const audioPulseId = ref(0);

const changeTranscriptionNoteIdDialog = useConfirmDialog();

const props = defineProps({
  resourceId: {
    type: String,
    required: true,
  },
  isQuickNote: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const {
  startRecording,
  stopRecording,
  startRecordingForAnotherNote,
  alreadyRecordedForCurrentNote,
  isRecordingStarted,
  isRecordingStopped,
  isRecordingPending,
  hasTranscripts,
  transcriptionState,
  currentTranscriptionNoteId,
  currentTranscriptionNoteTitle,
  hasNoteTranscriptionFeatureFlag,
  isAutoStartTranscriptionEnabled,
  isAutoStartTranscriptionModeAlways,
} = useAudioTranscription(toRef(props, 'resourceId'));

const isStartedForCurrentNote = computed(() => {
  return transcriptionState.value.noteId === props.resourceId && isRecordingStarted.value;
});

const isPendingForCurrentNote = computed(() => {
  return transcriptionState.value.noteId === props.resourceId && isRecordingPending.value;
});

const changeCurrentTranscriptionNoteId = async () => {
  await startRecordingForAnotherNote(props.resourceId);
  emit('startRecording', transcriptionState.value.noteId);
  changeTranscriptionNoteIdDialog.cancel();
};

const isElectronOnMac = computed(() => isElectronApp.value && isMac.value);

const transcriptControlLabel = computed(() => {
  if (isStartedForCurrentNote.value) {
    return translate('pause');
  }

  return hasTranscripts.value ? translate('resume') : translate('transcribe');
});

const isRecordingStartedOnAnotherNote = computed(() => {
  return isRecordingStarted.value && currentTranscriptionNoteId.value !== props.resourceId;
});

const transcriptControlTooltip = computed(() => {
  if (!isElectronOnMac.value) return 'transcribingOnlyAvailableOnMac';
  if (!hasNoteTranscriptionFeatureFlag.value) return 'transcriptionNotAvailableOnPlan';
  if (isRecordingStartedOnAnotherNote.value) return 'transcribeHereInsteadPositive';
  return !isRecordingStarted.value ? 'startTranscription' : 'stopTranscription';
});

const transcriptControlIcon = computed(() => {
  if (!hasNoteTranscriptionFeatureFlag.value && isElectronOnMac.value) return 'lock';
  return isRecordingStarted.value && isStartedForCurrentNote.value ? 'pause' : 'mic';
});

const handleOpenTranscripts = (): void => {
  emit('openTranscripts');
};

const isRecentNoteWithEvent = (noteId: string) => {
  const note = store.getters.getNotes[noteId] || {};
  if (!note.meeting_time || !note.fromCalendarEvent) return false;

  const currentTimestamp = Math.floor(Date.now() / 1000);

  const timeDiffInMinutes = Math.abs(currentTimestamp - note.meeting_time) / 60;
  const isInEventTimeRange =
    note.meeting_metadata?.start_time <= currentTimestamp &&
    note.meeting_metadata?.end_time >= currentTimestamp;

  store.dispatch('localUpdateNoteProp', {
    noteId: note.id,
    propertyName: 'fromCalendarEvent',
    value: false,
  });

  return timeDiffInMinutes <= MAX_MINUTES_DIFF_FOR_AUTO_RECORDING || isInEventTimeRange;
};

const startRecordingOnNoteChange = (noteId: string, fromMeetingNotification: boolean = false) => {
  /**
   * Special case where transcription is always started from note from meeting notification.
   * Has priority over other conditions. A note can be created from a meeting notification from an event,
   * or as a new note.
   */
  if (fromMeetingNotification || store.getters.getNotes[noteId]?.fromMeetingNotification)
    startRecordingWrapper(true);
  if (!isElectronOnMac.value || !isAutoStartTranscriptionEnabled.value) return;

  if (
    (noteId === constants.newNote && isAutoStartTranscriptionModeAlways.value) ||
    isRecentNoteWithEvent(noteId)
  ) {
    startRecordingWrapper(true);
  }
};

watch(toRef(props, 'resourceId'), () => {
  if (!isRecordingStarted.value) audioPulseId.value += 1;
});

watch(
  () => props.resourceId,
  (newValue) => {
    startRecordingOnNoteChange(newValue, route?.query?.fromMeetingNotification);
  }
);

onMounted(() => {
  startRecordingOnNoteChange(props.resourceId);
});

const isControlLabelDisabled = computed((): boolean => {
  return !isElectronOnMac.value || isRecordingPending.value;
});

const isControlTooltipDisabled = computed((): boolean => {
  return isRecordingPending.value;
});

const startRecordingWrapper = async (attachCurrentDate?: boolean): void => {
  if (!isRecordingStopped.value) return;

  await startRecording(null, attachCurrentDate);
  emit('startRecording', transcriptionState.value.noteId);
};

const controlLabelClick = async (): void => {
  if (isControlLabelDisabled.value) {
    return;
  } else if (!hasNoteTranscriptionFeatureFlag.value) {
    routeToUpgradePlan();
  } else if (isRecordingStopped.value) {
    startRecordingWrapper();
  } else if (isStartedForCurrentNote.value) {
    stopRecording();
    emit('stopRecording');
  } else {
    changeTranscriptionNoteIdDialog.reveal();
  }
};
</script>

<style lang="scss">
.trascription-control-wrapper {
  @apply flex items-center justify-center gap-x-2 flex-row;

  .transcript-button {
    @apply cursor-pointer flex items-center gap-1 py-1 px-2 rounded hover:bg-activeItem-default h-4;
  }
}
</style>
